import { useEffect, useRef, useState } from "react";

import { Box, OutlinedInput } from "@mui/material";

interface VerificationCodeInputProps {
  value: string;
  onChange: (code: string) => void;
  onComplete?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
}

/**
 * A 6-digit verification code input with individual boxes for each digit.
 * Supports auto-focus to next input, backspace navigation, and paste.
 */
export const VerificationCodeInput = ({
  value,
  onChange,
  onComplete,
  disabled = false,
  autoFocus = false,
}: VerificationCodeInputProps) => {
  const length = 6;

  // Convert string value to array of 6 characters
  const valueToArray = (val: string): string[] => {
    const arr = val.split("").slice(0, length);
    while (arr.length < length) {
      arr.push("");
    }
    return arr;
  };

  const [digits, setDigits] = useState<string[]>(() => valueToArray(value));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Sync internal state with external value when value prop changes
  useEffect(() => {
    setDigits(valueToArray(value));
  }, [value]);

  const handleChange = (index: number, inputValue: string) => {
    // Only allow single digit
    if (inputValue && !/^\d$/.test(inputValue)) {
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = inputValue;
    updateDigits(newDigits);

    // Auto-focus next input
    if (inputValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Handle backspace to go to previous input
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const updateDigits = (newDigits: string[]) => {
    setDigits(newDigits);
    onChange(newDigits.join(""));
    if (newDigits.every(d => d !== "") && onComplete) {
      onComplete();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData) {
      const newDigits = [...digits];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newDigits[i] = pastedData[i];
      }
      updateDigits(newDigits);

      // Focus the next empty input or the last one
      const nextEmptyIndex = newDigits.findIndex(d => d === "");
      inputRefs.current[nextEmptyIndex !== -1 ? nextEmptyIndex : 5]?.focus();
    }
  };

  return (
    <Box display="flex" gap={1.5} justifyContent="center">
      {digits.map((digit, index) => (
        <OutlinedInput
          key={index}
          value={digit}
          onChange={e => handleChange(index, e.target.value)}
          onKeyDown={e => handleKeyDown(index, e)}
          onPaste={index === 0 ? handlePaste : undefined}
          inputRef={el => (inputRefs.current[index] = el)}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          inputProps={{
            maxLength: 1,
            inputMode: "numeric",
            autoComplete: index === 0 ? "one-time-code" : "off",
            style: { textAlign: "center", fontSize: "20px", fontWeight: 600 },
            "data-testid": `code-input-${index}`,
          }}
          sx={{
            width: "56px",
            height: "56px",
          }}
        />
      ))}
    </Box>
  );
};
